import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { useAccount } from '../account';
import { ApiError } from '../api';
import type { RadioAchievement } from '../types';

type AccountMode = 'login' | 'register';

const ACHIEVEMENT_ICONS: Record<string, string> = {
  'signal-found': '✦',
  'first-hour': '01',
  'live-wire': '⚡',
  'archive-diver': '◉',
  'room-regular': '#',
  'first-drop': 'T',
};

function duration(seconds: number): string {
  if (seconds < 60) return `${Math.max(0, Math.floor(seconds))}s`;
  if (seconds < 3_600) return `${Math.floor(seconds / 60)}m`;
  const hours = seconds / 3_600;
  return `${hours >= 10 ? Math.floor(hours) : hours.toFixed(1)}h`;
}

function errorText(error: unknown): string | null {
  if (error instanceof ApiError) return error.detail;
  if (error instanceof Error) return error.message;
  return error ? 'The account request failed. Try once more.' : null;
}

function Achievement({ achievement }: { achievement: RadioAchievement }) {
  const percent = Math.min(100, Math.round((achievement.progress / achievement.target) * 100));
  return (
    <article className="radio-achievement" data-earned={achievement.earned ? 'true' : 'false'}>
      <div className="radio-achievement__mark" aria-hidden="true">
        <span>{ACHIEVEMENT_ICONS[achievement.slug] ?? 'HGR'}</span>
        {achievement.earned ? <i>✓</i> : null}
      </div>
      <div className="radio-achievement__copy">
        <span className="mono">{achievement.earned ? 'UNLOCKED' : `${percent}%`}</span>
        <h3>{achievement.title}</h3>
        <p>{achievement.description}</p>
        <div className="radio-achievement__track" aria-hidden="true">
          <i style={{ width: `${percent}%` }} />
        </div>
      </div>
    </article>
  );
}

export default function Account() {
  const accountState = useAccount();
  const [mode, setMode] = useState<AccountMode>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nextUsername, setNextUsername] = useState('');
  const ids = useId();
  const account = accountState.account;

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    try {
      if (mode === 'register') {
        await accountState.register(username.trim(), password);
      } else {
        await accountState.login(username.trim(), password);
      }
      setPassword('');
    } catch {
      // The provider keeps the exact API failure for the field-level surface.
    }
  };

  const rename = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    try {
      await accountState.rename(nextUsername.trim());
      setEditingName(false);
    } catch {
      // The availability or validation message remains in the provider.
    }
  };

  if (accountState.loading) {
    return (
      <section className="account-page account-page--loading" aria-live="polite">
        <div className="mono dim">FINDING YOUR FREQUENCY…</div>
      </section>
    );
  }

  if (!account) {
    const failure = errorText(accountState.error);
    const usernameFailure = accountState.error instanceof ApiError
      ? accountState.error.fields.username
      : null;
    const passwordFailure = accountState.error instanceof ApiError
      ? accountState.error.fields.password
      : null;
    return (
      <div className="account-page">
        <section className="sec account-hero">
          <div>
            <div className="mono dim">ONE ID · THE WHOLE STATION</div>
            <h1>Higher Ground account</h1>
          </div>
          <p className="mono sec__meta">LISTEN · CHAT · COLLECT · UNLOCK</p>
        </section>

        <div className="account-auth">
          <section className="account-auth__pitch">
            <span className="mono">YOUR FREQUENCY</span>
            <h2>One profile everywhere.</h2>
            <p>
              Your listening hours, dancefloor identity, collected clothes and achievements stay
              together on every page and every device.
            </p>
            <ul className="mono">
              <li>Listening time split between live and archive</li>
              <li>Badges for tuning in, chatting and collecting drops</li>
              <li>The same username and inventory inside the live room</li>
            </ul>
          </section>

          <section className="account-auth__form" aria-labelledby={`${ids}-title`}>
            <div className="account-tabs" role="tablist" aria-label="Account action">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'login'}
                onClick={() => { setMode('login'); accountState.clearError(); }}
              >
                Login
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'register'}
                onClick={() => { setMode('register'); accountState.clearError(); }}
              >
                Create account
              </button>
            </div>
            <div className="account-auth__formhead">
              <span className="mono">{mode === 'register' ? 'NEW LISTENER' : 'WELCOME BACK'}</span>
              <h2 id={`${ids}-title`}>
                {mode === 'register' ? 'Save your signal.' : 'Return to the frequency.'}
              </h2>
            </div>
            <form onSubmit={(event) => void submit(event)}>
              <label className="mono" htmlFor={`${ids}-username`}>Username</label>
              <input
                id={`${ids}-username`}
                value={username}
                minLength={3}
                maxLength={24}
                autoComplete="username"
                aria-invalid={usernameFailure ? 'true' : undefined}
                onChange={(event) => { setUsername(event.target.value); accountState.clearError(); }}
                required
              />
              {usernameFailure ? <p className="account-field-error">{usernameFailure}</p> : null}
              <label className="mono" htmlFor={`${ids}-password`}>Password</label>
              <input
                id={`${ids}-password`}
                type="password"
                value={password}
                minLength={8}
                maxLength={128}
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                aria-invalid={passwordFailure ? 'true' : undefined}
                onChange={(event) => { setPassword(event.target.value); accountState.clearError(); }}
                required
              />
              {passwordFailure ? <p className="account-field-error">{passwordFailure}</p> : null}
              <p className="mono dim account-auth__requirements">
                Username 3–24 characters · Password at least 8 characters
              </p>
              {failure && !usernameFailure && !passwordFailure ? (
                <p className="account-form-error" role="alert">{failure}</p>
              ) : null}
              <button className="btn" type="submit" disabled={accountState.busy}>
                {accountState.busy
                  ? 'ONE SEC…'
                  : mode === 'register' ? 'CREATE ACCOUNT' : 'LOGIN'}
              </button>
            </form>
          </section>
        </div>
      </div>
    );
  }

  const earned = account.achievements.filter((item) => item.earned).length;
  return (
    <div className="account-page">
      <section className="sec account-hero account-hero--signed">
        <div>
          <div className="mono dim">SIGNED IN ACROSS HIGHER GROUND</div>
          <h1>@{account.username}</h1>
        </div>
        <div className="account-identitytools">
          <div className="account-rank">
            <span className="mono">CURRENT RANK</span>
            <strong>{account.stats.rank}</strong>
          </div>
          <button
            type="button"
            className="btn-ghost account-editname"
            onClick={() => {
              setNextUsername(account.username);
              setEditingName((open) => !open);
              accountState.clearError();
            }}
          >
            {editingName ? 'CANCEL' : 'CHANGE NAME'}
          </button>
        </div>
      </section>

      {editingName ? (
        <section className="account-rename" aria-label="Change username">
          <div>
            <span className="mono dim">PUBLIC IDENTITY</span>
            <strong>Choose an available name.</strong>
            <p>This also becomes your name in the station chat.</p>
          </div>
          <form onSubmit={(event) => void rename(event)}>
            <label className="vh" htmlFor={`${ids}-new-username`}>New username</label>
            <input
              id={`${ids}-new-username`}
              value={nextUsername}
              minLength={3}
              maxLength={24}
              autoComplete="username"
              aria-invalid={accountState.error ? 'true' : undefined}
              onChange={(event) => {
                setNextUsername(event.target.value);
                accountState.clearError();
              }}
              required
            />
            <button className="btn" type="submit" disabled={accountState.busy}>
              {accountState.busy ? 'CHECKING…' : 'SAVE NAME'}
            </button>
          </form>
          {accountState.error ? (
            <p className="account-form-error" role="alert">{errorText(accountState.error)}</p>
          ) : null}
        </section>
      ) : null}

      <section className="account-stats" aria-label="Listening statistics">
        <article><span className="mono">TOTAL TUNED IN</span><strong>{duration(account.stats.total_seconds)}</strong><small>all listening</small></article>
        <article><span className="mono">LIVE SIGNAL</span><strong>{duration(account.stats.live_seconds)}</strong><small>live broadcasts</small></article>
        <article><span className="mono">ARCHIVE</span><strong>{duration(account.stats.archive_seconds)}</strong><small>recorded sets</small></article>
        <article><span className="mono">ROOM SIGNALS</span><strong>{account.stats.message_count}</strong><small>visible messages</small></article>
      </section>

      <section className="account-section">
        <header>
          <div><span className="mono dim">PROGRESS</span><h2>Badges & achievements</h2></div>
          <strong className="mono">{earned}/{account.achievements.length} UNLOCKED</strong>
        </header>
        <div className="radio-achievements">
          {account.achievements.map((achievement) => (
            <Achievement key={achievement.slug} achievement={achievement} />
          ))}
        </div>
      </section>

      <section className="account-section account-collection">
        <header>
          <div><span className="mono dim">OWNED FOREVER</span><h2>Your collection</h2></div>
          <strong className="mono">{account.stats.items_collected} ITEMS</strong>
        </header>
        {account.inventory.length ? (
          <div className="account-collection__grid">
            {account.inventory.map((item) => (
              <article key={item.slug}>
                <i aria-hidden="true" />
                <div><strong>{item.name}</strong><span className="mono dim">{item.description}</span></div>
              </article>
            ))}
          </div>
        ) : (
          <p className="account-empty">
            Your collection is empty. Limited items will drop during selected live streams.
          </p>
        )}
      </section>

      <section className="account-footerline">
        <p className="mono dim">
          MEMBER SINCE {new Date(account.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }).toUpperCase()}
        </p>
        <div>
          <Link className="btn-ghost" to="/">Back to live</Link>
          <button
            type="button"
            className="btn"
            disabled={accountState.busy}
            onClick={() => void accountState.logout()}
          >
            {accountState.busy ? 'ONE SEC…' : 'LOG OUT'}
          </button>
        </div>
      </section>
    </div>
  );
}
